import React, { useEffect, useState } from "react";
import { Loader2, Lock, Gift, Star, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { memberApi, imgUrl, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import VideosSection from "./VideosView";

const dateFmt = (iso) => new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });

function usePosts(type) {
  const [posts, setPosts] = useState(null);
  const load = async () => {
    try { const { data } = await memberApi.get("/member/posts", { params: { type } }); setPosts(data); }
    catch (e) { toast.error(errMsg(e, "Could not load")); setPosts([]); }
  };
  useEffect(() => { load(); }, [type]); // eslint-disable-line
  return [posts, setPosts, load];
}

export const Page = ({ title, sub, children, icon }) => (
  <div className="px-5 lg:px-10 py-7 lg:py-9 max-w-[980px] pb-28 lg:pb-10 mem-fade">
    <h1 className="mem-brand text-[32px] lg:text-[38px] flex items-center gap-3">{title} {icon || <span className="text-[#e8365f]">♥</span>}</h1>
    {sub && <p className="text-[15px] text-[#6b5d60] mt-1">{sub}</p>}
    <div className="mt-7">{children}</div>
  </div>
);

const Loading = () => <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#e8365f]" /></div>;
const Empty = ({ text }) => <div className="mem-card p-10 text-center text-[#6b5d60]" data-testid="mem-empty"><ImageOff className="w-7 h-7 mx-auto mb-2" />{text}</div>;

function Viewer({ src, onClose }) {
  return (
    <Dialog open={!!src} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[560px] p-0 overflow-hidden bg-black border-0">
        <DialogTitle className="sr-only">Photo</DialogTitle>
        {src && <img src={src} alt="" className="w-full max-h-[85vh] object-contain" />}
      </DialogContent>
    </Dialog>
  );
}

function PhotoPost({ p, onOpen }) {
  return (
    <article className="mem-card p-5" data-testid="mem-post">
      <div className="flex items-baseline justify-between"><h3 className="text-[18px] font-semibold">{p.title}</h3><span className="text-[12px] text-[#9a8b8e]">{dateFmt(p.created_at)}</span></div>
      {p.text && <p className="text-[14.5px] text-[#4a3d40] mt-1">{p.text}</p>}
      <div className={`grid gap-3 mt-4 ${p.images.length > 1 ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 max-w-[360px]"}`}>
        {p.images.map((u, i) => (
          <button key={i} onClick={() => onOpen(imgUrl(u))} className="aspect-[3/4] rounded-xl overflow-hidden"><img src={imgUrl(u)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /></button>
        ))}
      </div>
    </article>
  );
}

export function FromMeView() {
  const [posts] = usePosts("from_me");
  const [v, setV] = useState(null);
  return (
    <Page title="From Me" sub="Exclusive photos I share only with my VIPs">
      {!posts ? <Loading /> : !posts.length ? <Empty text="Nothing here yet... soon!" /> : <div className="space-y-5">{posts.map((p) => <PhotoPost key={p.id} p={p} onOpen={setV} />)}</div>}
      <Viewer src={v} onClose={() => setV(null)} />
    </Page>
  );
}

export function ForYouView({ user }) {
  const [posts] = usePosts("for_you");
  const [v, setV] = useState(null);
  return (
    <Page title="For You" sub={`VIP videos and things I made just for you, ${user.nickname || user.name}`} icon={<Star className="w-7 h-7 text-[#e8365f]" fill="#f9c9d4" />}>
      <VideosSection />
      <h2 className="mem-brand text-[24px] mb-4">Just for you <span className="text-[#e8365f]">♥</span></h2>
      {!posts ? <Loading /> : !posts.length ? <Empty text="Your personal surprises from me will appear here." /> : <div className="space-y-5">{posts.map((p) => <PhotoPost key={p.id} p={p} onOpen={setV} />)}</div>}
      <Viewer src={v} onClose={() => setV(null)} />
    </Page>
  );
}

export function PollCard({ p, onVoted }) {
  const total = (p.counts || []).reduce((a, b) => a + b, 0);
  const voted = p.my_vote !== null && p.my_vote !== undefined;
  const vote = async (i) => {
    try { const { data } = await memberApi.post(`/member/polls/${p.id}/vote`, { option: i }); onVoted(data); toast.success("Vote saved"); }
    catch (e) { toast.error(errMsg(e, "Could not vote")); }
  };
  return (
    <article className="mem-card p-5 flex flex-col sm:flex-row gap-5" data-testid="mem-poll">
      {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="w-full sm:w-[200px] h-[240px] rounded-xl object-cover" />}
      <div className="flex-1">
        <h3 className="text-[19px] font-semibold">{p.title}</h3>
        {p.text && <p className="text-[14px] text-[#6b5d60] mt-1">{p.text}</p>}
        <div className="space-y-2.5 mt-4">
          {p.options.map((o, i) => {
            const pct = total ? Math.round(((p.counts?.[i] || 0) / total) * 100) : 0;
            return (
              <button key={i} onClick={() => vote(i)} className={`mem-opt relative overflow-hidden !text-left !px-4 !h-11 ${p.my_vote === i ? "mem-opt-on" : ""}`} data-testid={`poll-opt-${i}`}>
                {voted && <span className="absolute inset-y-0 left-0 bg-[#fbd3dc]/70" style={{ width: `${pct}%` }} />}
                <span className="relative flex justify-between"><span>{o}</span>{voted && <span className="text-[12px] font-semibold">{pct}%</span>}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[12px] text-[#9a8b8e] mt-3">{total} vote{total === 1 ? "" : "s"} · {dateFmt(p.created_at)}</p>
      </div>
    </article>
  );
}

export function ChooseView({ onChange }) {
  const [posts, setPosts] = usePosts("poll");
  const onVoted = (np) => { setPosts((ps) => ps.map((x) => (x.id === np.id ? np : x))); onChange?.(); };
  return (
    <Page title="Choose for Me" sub="You decide, I'll do it">
      {!posts ? <Loading /> : !posts.length ? <Empty text="No polls right now." /> : <div className="space-y-5">{posts.map((p) => <PollCard key={p.id} p={p} onVoted={onVoted} />)}</div>}
    </Page>
  );
}

export function UpdatesView() {
  const [posts] = usePosts("update");
  return (
    <Page title="My Updates" sub="Little pieces of my day">
      {!posts ? <Loading /> : !posts.length ? <Empty text="No updates yet." /> : (
        <div className="space-y-5">
          {posts.map((p) => (
            <article key={p.id} className="mem-card p-5 flex flex-col sm:flex-row gap-5" data-testid="mem-update">
              {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="w-full sm:w-[220px] h-[220px] rounded-xl object-cover" />}
              <div className="flex-1">
                <p className="text-[12px] text-[#9a8b8e]">{dateFmt(p.created_at)}</p>
                <h3 className="text-[19px] font-semibold mt-1">{p.title}</h3>
                <p className="text-[15px] leading-[1.7] text-[#3a2d30] mt-2 whitespace-pre-wrap">{p.text}</p>
                <p className="mem-hand text-[30px] mt-2">- Maya</p>
              </div>
            </article>
          ))}
        </div>
      )}
    </Page>
  );
}

export function SurprisesView({ onChange }) {
  const [posts, setPosts] = usePosts("surprise");
  const [opening, setOpening] = useState(null);
  const open = async (p) => {
    setOpening(p.id);
    try {
      const { data } = await memberApi.post(`/member/surprises/${p.id}/open`);
      setPosts((ps) => ps.map((x) => (x.id === p.id ? data : x)));
      onChange?.();
    } catch (e) { toast.error(errMsg(e, "Could not open")); }
    finally { setOpening(null); }
  };
  return (
    <Page title="Surprises" sub="Tap a gift to open it" icon={<Gift className="w-7 h-7 text-[#e8365f]" fill="#f9c9d4" />}>
      {!posts ? <Loading /> : !posts.length ? <Empty text="No surprises yet... stay tuned." /> : (
        <div className="grid sm:grid-cols-2 gap-5">
          {posts.map((p) => (
            <article key={p.id} className="mem-card overflow-hidden" data-testid="mem-surprise">
              <div className="relative h-[260px] overflow-hidden">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className={`w-full h-full object-cover transition-all duration-700 ${p.opened ? "" : "blur-xl scale-110"}`} />}
                {!p.opened && (
                  <button onClick={() => open(p)} className="absolute inset-0 grid place-items-center bg-black/30 text-white" data-testid="surprise-open">
                    <span className="flex flex-col items-center gap-2">{opening === p.id ? <Loader2 className="w-8 h-8 animate-spin" /> : <Lock className="w-8 h-8" />}<span className="text-[15px] font-medium">Tap to open</span></span>
                  </button>
                )}
              </div>
              <div className="p-5">
                <h3 className="text-[18px] font-semibold">{p.title}</h3>
                {p.opened ? <p className="text-[14.5px] text-[#3a2d30] mt-1">{p.text}</p> : <p className="text-[14px] text-[#9a8b8e] mt-1">A little something is waiting for you...</p>}
              </div>
            </article>
          ))}
        </div>
      )}
    </Page>
  );
}
